/**
 * Global modal stack.
 *
 * Lets any component open a modal without owning `open` state or having
 * to render the modal next to itself in the tree. `openModal` pushes a
 * component + props onto a stack; the provider renders each entry once,
 * at the root of the app, above all route content.
 *
 * Usage:
 *
 *   const { openModal } = useModal()
 *   openModal(WorkflowIssueResolveModal, { uuid })
 *
 * Modals must accept the standard `open: boolean; onOpenChange: (open: boolean) => void`
 * contract (the same contract every `Dialog`-based modal already
 * implements). The provider drives `open` so closing is animated via
 * the Dialog primitive, then unmounts the entry once the exit animation
 * has had time to play.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react'

/** Minimum contract every modal registered here must satisfy. */
export interface ModalComponentProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface ModalEntry<P extends ModalComponentProps = ModalComponentProps> {
  id: string
  Component: ComponentType<P>
  props: Omit<P, 'open' | 'onOpenChange'>
  open: boolean
}

interface ModalContextValue {
  /**
   * Mount a modal at the root of the app. Returns an id that can be
   * passed to `closeModal` if the caller needs to close the modal
   * imperatively (most callers don't - the user clicking ✕ or outside
   * the modal triggers close automatically).
   */
  openModal: <P extends ModalComponentProps>(
    Component: ComponentType<P>,
    props: Omit<P, 'open' | 'onOpenChange'>,
  ) => string
  /** Close a specific modal by id, or the top of the stack if omitted. */
  closeModal: (id?: string) => void
}

const ModalContext = createContext<ModalContextValue | null>(null)

// Matches the exit-animation duration the Dialog primitive uses; once
// the modal has played its close animation we unmount the entry so the
// stack doesn't keep invisible components alive.
const CLOSE_ANIMATION_MS = 220

export function ModalProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<ModalEntry[]>([])
  // Track pending unmount timers so we can cancel them if the same
  // entry is re-opened before the animation finishes.
  const unmountTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const closeModal = useCallback((id?: string) => {
    setStack((current) => {
      if (current.length === 0) return current
      const targetId = id ?? current[current.length - 1]?.id
      if (!targetId) return current
      return current.map((entry) =>
        entry.id === targetId ? { ...entry, open: false } : entry,
      )
    })
    const targetId = id
    const timer = setTimeout(() => {
      setStack((current) =>
        targetId
          ? current.filter((entry) => entry.id !== targetId)
          : current.filter((entry) => entry.open),
      )
    }, CLOSE_ANIMATION_MS)
    if (targetId) unmountTimers.current.set(targetId, timer)
  }, [])

  const openModal = useCallback<ModalContextValue['openModal']>(
    (Component, props) => {
      const id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `modal-${Date.now()}-${Math.random().toString(36).slice(2)}`
      setStack((current) => [
        ...current,
        { id, Component: Component as ComponentType<ModalComponentProps>, props, open: true },
      ])
      return id
    },
    [],
  )

  const value = useMemo<ModalContextValue>(
    () => ({ openModal, closeModal }),
    [openModal, closeModal],
  )

  return (
    <ModalContext.Provider value={value}>
      {children}
      {stack.map((entry) => {
        const Component = entry.Component
        return (
          <Component
            key={entry.id}
            {...(entry.props as ModalComponentProps)}
            open={entry.open}
            onOpenChange={(next) => {
              if (!next) closeModal(entry.id)
            }}
          />
        )
      })}
    </ModalContext.Provider>
  )
}

/**
 * Access the global modal stack. Must be called inside a `<ModalProvider>`
 * - the provider lives at the app root, so this is always true in
 * normal render paths.
 */
// Colocating the hook with the provider is the standard React pattern
// and matches how other providers in this codebase export their hooks;
// splitting them would force every consumer to import from two files.
// eslint-disable-next-line react-refresh/only-export-components
export function useModal(): ModalContextValue {
  const ctx = useContext(ModalContext)
  if (!ctx) {
    throw new Error('useModal must be used within a <ModalProvider>')
  }
  return ctx
}
